import React from 'react';
import PropTypes from 'prop-types';
import { Button } from '@cbrebuild/blocks';
import uniqueId from 'lodash/uniqueId';
import userEventService from '../../services/user-event-service';
import Toast from '../../nucleus/toast/toast';

import Dropzone from '../../nucleus/dropzone/dropzone';
import FileToUploadListItem from './file-to-upload-list-item';
import Notice from '../../nucleus/notices/notice';
import dealsService from '../../services/deals-service';

const isOfacFileName = filename => Boolean(filename && filename.toLowerCase().includes('ofac'));

class FileUpload extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      files: [],
      isUploadingFiles: false,
      showFileLimitExceededNotice: false,
      toasts: [],
      toastIndex: 0,
      currentToast: null,
      progressIntervals: {},
    };
    this.toastTimeout = null;
  }

  componentDidMount() {
    // Lease abstraction is now always available, no need to check feature flags
  }


  componentWillUnmount() {
    if (this.toastTimeout) {
      clearTimeout(this.toastTimeout);
    }

    // Clear all progress intervals
    Object.values(this.state.progressIntervals).forEach((interval) => {
      if (interval) {
        clearInterval(interval);
      }
    });
  }

  showToastsSequentially = (responses) => {
    if (!responses || responses.length === 0) return;
    this.setState({
      toasts: responses.map((file) => {
        if (!file) return null; // Skip if file upload failed
        const trimmedName = file.name.length > 15 ? `${file.name.slice(0, 15)}...` : file.name;
        return `${trimmedName} uploaded successfully`;
      }),
      toastIndex: 0,
    }, this.displayNextToast);
  };

  displayNextToast = () => {
    const TOAST_DURATION = 5000;
    const TOAST_GAP = 500;

    if (this.state.toastIndex < this.state.toasts.length) {
      this.setState({ currentToast: this.state.toasts[this.state.toastIndex] });
      this.toastTimeout = setTimeout(() => {
        this.setState(
          () => ({ currentToast: null }),
          () => {
            this.toastTimeout = setTimeout(() => {
              this.setState(
                prevState => ({ toastIndex: prevState.toastIndex + 1 }),
                this.displayNextToast,
              );
            }, TOAST_GAP);
          },
        );
      }, TOAST_DURATION);
    } else {
      this.setState({ currentToast: null, toasts: [], toastIndex: 0 });
    }
  };

  dismissNotice = () => {
    this.setState({ showFileLimitExceededNotice: false });
  }

  isUploadButtonDisabled = () => {
    const { isUploadingFiles, files } = this.state;
    // Disable if already uploading or if any file has empty/null name
    return isUploadingFiles || files.some(file => !file.name || !file.name.trim());
  }

  handleFilesAdded = (fileDataArr) => {
    const { isEnhancedFlow, dealType } = this.props;
    const files = fileDataArr
      .filter((fileData) => {
        if (fileData.size > 1073741824) {
          this.setState({ showFileLimitExceededNotice: true });
          return false;
        }
        return true;
      })
      .map(fileData => ({
        fileData,
        // fileData is a JS file object so be wary as it has some special
        // behavior (i.e. cannot be spread over like regular objects)
        isPrivate: false,
        name: fileData.name, // user chosen attachment name, default to uploaded file name
        tempId: uniqueId(),
        uploadProgress: 0,
        // In the enhanced flow, PDFs are opted in unless the document is OFAC-related.
        optInForExtraction: !!(
          isEnhancedFlow &&
          dealType === 'lease' &&
          fileData.name &&
          fileData.name.toLowerCase().endsWith('.pdf') &&
          !isOfacFileName(fileData.name)
        ),
      }));
    this.setState(prevState => ({
      files: prevState.files.concat(files),
    }));
  }

  handleRemove = (tempId) => {
    const {
      dealId,
    } = this.props;

    // Clear progress interval for this file if it exists
    if (this.state.progressIntervals[tempId]) {
      clearInterval(this.state.progressIntervals[tempId]);
    }

    this.setState({
      files: this.state.files.filter((file => file.tempId !== tempId)),
      progressIntervals: {
        ...this.state.progressIntervals,
        [tempId]: undefined,
      },
    });

  }

  handleFileNameChange = (tempId, value) => {
    const {
      dealId,
    } = this.props;

    this.setState({
      files: this.state.files.map(file => (file.tempId === tempId
        ? ({ ...file, name: value, optInForExtraction: isOfacFileName(value) ? false : file.optInForExtraction })
        : file)),
    });

  }

  handlePrivacyChange = (tempId, value) => {
    const {
      dealId,
    } = this.props;

    this.setState({
      files: this.state.files.map(file => (file.tempId === tempId
        ? ({ ...file, isPrivate: value })
        : file)),
    });

  }

  handleExtractionOptInChange = (tempId, value) => {
    const {
      dealId,
    } = this.props;

    this.setState({
      files: this.state.files.map(file => (file.tempId === tempId
        ? ({ ...file, optInForExtraction: isOfacFileName(file.name) ? false : value })
        : file)),
    });

  }

  handleProgress = (progressEvent, tempId) => {
    const uploadProgress = (progressEvent.loaded / progressEvent.total) * 100;
    this.setState({
      files: this.state.files.map(file => (file.tempId === tempId
        ? ({ ...file, uploadProgress })
        : file)),
    });
  };

  handleUploadFiles = async () => {
    const { files } = this.state;
    const { dealId } = this.props;
    this.setState({ isUploadingFiles: true });
    const promises = files.map(this.sendRequest);
    await Promise.all(promises)
      .then((response) => {
        this.showToastsSequentially(response);
        this.trackUpload(files.length);
      });

    // Clear all progress intervals
    Object.values(this.state.progressIntervals).forEach((interval) => {
      clearInterval(interval);
    });

    this.setState({
      isUploadingFiles: false,
      files: [],
      progressIntervals: {},
    });
  }

  sendRequest = file => new Promise(async (resolve) => {
    const {
      dealId,
      isEnhancedFlow,
      uploadFile,
    } = this.props;
    const {
      fileData,
      isPrivate,
      name,
      tempId,
      optInForExtraction,
    } = file;
    const params = {
      deal: dealId,
      title: name,
      // document_name is not mutable, it should
      // always be the original file name
      document_name: fileData.name,
      is_lease_document: false,
      privacy: isPrivate ? 'private' : 'team',
      extraction_opted_in: optInForExtraction && !isOfacFileName(name) && !isOfacFileName(fileData.name), // boolean field for backend
    };

    // Simulate progress since backend upload doesn't provide real progress
    this.simulateProgress(tempId, fileData.size);

    const shouldOptInForExtraction = optInForExtraction && !isOfacFileName(name) && !isOfacFileName(fileData.name);
    const response = await uploadFile(fileData, params, e => this.handleProgress(e, tempId), shouldOptInForExtraction, isEnhancedFlow);

    // Clear the progress interval and set to 100% when upload completes
    if (this.state.progressIntervals[tempId]) {
      clearInterval(this.state.progressIntervals[tempId]);
    }

    // Set progress to 100% when upload is complete
    this.setState({
      files: this.state.files.map(fileState => (fileState.tempId === tempId
        ? ({ ...fileState, uploadProgress: 100 })
        : fileState)),
      progressIntervals: {
        ...this.state.progressIntervals,
        [tempId]: undefined,
      },
    });

    if (response) {
      resolve({ name, size: fileData.size, type: fileData.type });
    } else {
      resolve();
    }
    this.trackSize(fileData.size);
    this.trackType(fileData.type);
  })

  simulateProgress = (tempId, fileSize) => {
    // Simulate progress based on file size with realistic timing
    // Small files: ~1-2 seconds, Medium files: ~2-5 seconds, Large files: ~5-10 seconds
    const baseTime = 1000; // 1 second minimum
    const timePerMB = 300; // 300ms per MB (more reasonable than 500ms)
    const fileSizeInMB = fileSize / (1024 * 1024);
    const totalTime = Math.min(baseTime + (fileSizeInMB * timePerMB), 10000); // Cap at 10 seconds
    const steps = 20; // Number of progress updates
    const interval = totalTime / steps;

    let currentProgress = 0;
    const progressInterval = setInterval(() => {
      // Slow progress until 65%, then faster progress from 65% to 95%
      let progressIncrement;
      if (currentProgress < 65) {
        // Slow phase: smaller increments until 65%
        progressIncrement = (65 / steps) * 0.6; // 60% of normal speed until 65%
      } else {
        // Fast phase: larger increments from 65% to 95%
        progressIncrement = ((95 - 65) / (steps * 0.4)) * 1.5; // 1.5x speed for remaining 30%
      }

      currentProgress += progressIncrement;

      // Add some randomness to make it feel more realistic, but less variation in slow phase
      const randomFraction = crypto.getRandomValues(new Uint32Array(1))[0] / 0xffffffff;
      const randomOffset = randomFraction - 0.5;
      const randomVariation = randomOffset * (currentProgress < 65 ? 5 : 8);

      const adjustedProgress = Math.min(95, Math.max(0, currentProgress + randomVariation));

      this.setState({
        files: this.state.files.map(file => (file.tempId === tempId
          ? ({ ...file, uploadProgress: adjustedProgress })
          : file)),
      });

      // Stop at 95% and let the actual upload completion set it to 100%
      if (currentProgress >= 95) {
        clearInterval(progressInterval);
      }
    }, interval);

    // Store the interval so we can clear it if needed
    this.setState(prevState => ({
      ...prevState,
      progressIntervals: {
        ...prevState.progressIntervals,
        [tempId]: progressInterval,
      },
    }));
  }

  trackType = (eventValue) => {
    const {
      analyticProperties: {
        actionPrefix,
        categoryPrefix,
      },
    } = this.props;
    userEventService.trackEvent(
      {
        eventAction: 'file_type_uploaded',
        eventCategory: 'File Action',
        eventValue,
      },
      {
        actionPrefix,
        categoryPrefix,
      },
    );
  }

  trackSize = (eventValue) => {
    const {
      analyticProperties: {
        actionPrefix,
        categoryPrefix,
      },
    } = this.props;
    userEventService.trackEvent(
      {
        eventAction: 'file_size_uploaded',
        eventCategory: 'File Action',
        eventValue,
      },
      {
        actionPrefix,
        categoryPrefix,
      },
    );
  }

  trackUpload = (eventValue) => {
    const {
      analyticProperties: {
        actionPrefix,
        categoryPrefix,
      },
    } = this.props;
    userEventService.trackEvent(
      {
        eventAction: 'files_uploaded',
        eventCategory: 'File Action',
        eventValue,
      },
      {
        actionPrefix,
        categoryPrefix,
      },
    );
  }

  render() {
    const {
      files,
      isUploadingFiles,
      showFileLimitExceededNotice,
    } = this.state;
    const { dealType, isEnhancedFlow } = this.props;

    return (
      // has to be named deal-file-upload because of namespace
      // issue with file-upload
      <div className="deal-file-upload">
        <div className="dropzone-wrapper">
          <Dropzone
            onFilesAdded={this.handleFilesAdded}
            disabled={isUploadingFiles}
          />
        </div>
        <ul>
          {showFileLimitExceededNotice &&
            <Notice
              text="Max file size is 1GB. Some of your files were removed from the list."
              onDismiss={this.dismissNotice}
              type="warning"
            />
          }
          {files.map(file => (
            <FileToUploadListItem
              key={file.tempId}
              file={file}
              dealType={dealType}
              isEnhancedFlow={isEnhancedFlow}
              isUploading={isUploadingFiles}
              onFileNameChange={value => this.handleFileNameChange(file.tempId, value)}
              onPrivacyChange={value => this.handlePrivacyChange(file.tempId, value)}
              onExtractionOptInChange={value => this.handleExtractionOptInChange(file.tempId, value)}
              onRemove={value => this.handleRemove(file.tempId, value)}
            />
          ))}
        </ul>
        {files.length > 0 &&
          <div className="actions">
            <Button
              disabled={this.isUploadButtonDisabled()}
              onClick={this.handleUploadFiles}
            > Upload
            </Button>
          </div>
        }
        {this.state.currentToast && (
          <div className="toast-container">
            <Toast message={this.state.currentToast} />
          </div>
        )}
      </div>
    );
  }
}

FileUpload.propTypes = {
  analyticProperties: PropTypes.shape({
    actionPrefix: PropTypes.string,
    categoryPrefix: PropTypes.string,
  }).isRequired,
  dealId: PropTypes.number.isRequired,
  dealType: PropTypes.string,
  isEnhancedFlow: PropTypes.bool,
  uploadFile: PropTypes.func.isRequired,
};

FileUpload.defaultProps = {
  dealType: null,
  isEnhancedFlow: false,
};

export default FileUpload;
